import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Clock, Inbox, Loader2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import ProfileLink from '../../../components/ProfileLink'
import { useTeamStore } from '../store/useTeamStore'
import { personName, teamErrorText } from '../utils/teamFormat'
import { accentColor, cardPrimaryColor, contentBgColor } from '../utils/teamColors'
import { formatAgo } from '../../profile/utils/profileFormat'
import { formatSecondsLeft } from '../../../utils/formatters'
import { CARD_CLASS } from '../utils/teamUi'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

/**
 * Входящие заявки — только модератору и выше.
 * `applications: null` значит «не положено видеть» (обычный участник или чужая
 * банда), `[]` — заявок нет. В обоих случаях блок не рендерится.
 * Приём и отказ возвращают свежую карточку банды,
 * поэтому списки состава и заявок не перезапрашиваются.
 */
const TeamApplicationsList = ({ team }) => {
  const { t } = useTranslation()
  const { decideApplication } = useTeamStore()
  const [pendingId, setPendingId] = useState(null)

  const applications = team.applications
  if (!Array.isArray(applications) || applications.length === 0) return null

  const colors = team.colors
  const accent = accentColor(colors)
  const primary = cardPrimaryColor(colors)
  const contentBg = contentBgColor(colors)
  const subtleSurface = `${primary}0D`
  const subtleBorder = `${primary}1F`

  const handleDecide = async (id, approve) => {
    if (pendingId != null) return
    hapticImpact('medium')
    setPendingId(id)
    const res = await decideApplication(id, approve)
    setPendingId(null)

    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal(approve ? 'team-application-approve' : 'team-application-reject')
      toast.success(approve
        ? t('team.applicationApproved', 'Игрок принят в банду')
        : t('team.applicationRejected', 'Заявка отклонена'))
    } else if (res) {
      // team_full — мест нет, заявка остаётся активной и её можно принять позже
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  return (
    <div className={`${CARD_CLASS} p-4`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: primary }}>
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}1A` }}>
          <Inbox size={13} style={{ color: accent }} />
        </span>
        {t('team.applications', 'Заявки')}
        <span className="tabular-nums">{applications.length}</span>
      </h2>

      <div className="space-y-2">
        {applications.map((app) => {
          const name = personName(app.user, t)
          const isPending = pendingId === app.id

          return (
            <div
              key={app.id}
              className="rounded-xl border p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}
            >
              <div className="flex items-center gap-3">
                <ProfileLink slug={app.user?.slug} className="shrink-0">
                  <span className="relative block">
                    <span className="block h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-surface">
                      {app.user?.ava ? (
                        <img src={app.user.ava} alt={name} className="h-full w-full object-cover" />
                      ) : null}
                    </span>
                    {app.user?.online && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 block h-3 w-3 rounded-full border-2 border-dark-primary"
                        style={{ backgroundColor: accent }}
                      />
                    )}
                  </span>
                </ProfileLink>

                <div className="min-w-0 flex-1">
                  <ProfileLink slug={app.user?.slug} className="block">
                    <span className="block truncate text-sm font-semibold" style={{ color: primary }}>{name}</span>
                  </ProfileLink>
                  <span className="flex items-center gap-2 text-[11px]" style={{ color: primary }}>
                    <span>{formatAgo(app.created_ago, t)}</span>
                    {app.expires_in != null && (
                      <span className="flex items-center gap-1">
                        <Clock size={10} />
                        {formatSecondsLeft(app.expires_in, t)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {app.text && (
                <p className="mt-2 whitespace-pre-line break-words text-xs" style={{ color: primary }}>
                  {app.text}
                </p>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => handleDecide(app.id, true)}
                  disabled={isPending}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-accent-primary to-accent-secondary px-3 py-2 text-xs font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  {t('team.approve', 'Принять')}
                </button>
                <button
                  onClick={() => handleDecide(app.id, false)}
                  disabled={isPending}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-400 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <X size={13} />
                  {t('team.reject', 'Отклонить')}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TeamApplicationsList
